// ============================================================
//  Auth context — fournit l'user courant et bootstrap au mount
// ============================================================

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import * as api from "./api";
import type { Profile } from "./types";

type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "authed"; user: Profile };

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, full_name?: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    if (!api.getToken()) {
      setState({ status: "anon" });
      return;
    }
    api.fetchMe()
      .then((user) => setState({ status: "authed", user }))
      .catch(() => {
        api.clearTokens();
        setState({ status: "anon" });
      });
  }, []);

  const value: AuthContextValue = {
    ...state,
    login: async (email, password) => {
      const user = await api.login(email, password);
      setState({ status: "authed", user });
    },
    register: async (email, password, full_name) => {
      const user = await api.register(email, password, full_name);
      setState({ status: "authed", user });
    },
    logout: async () => {
      await api.logout();
      setState({ status: "anon" });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors AuthProvider");
  return ctx;
}
